import {
  Injectable,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { classifyWineSuiteDeviceType } from '@util/dws-device-classification';
import { WsTooltipPanelComponent } from './ws-tooltip-panel.component';
import {
  WsTooltipContent,
  WsTooltipTemplateContext,
  isWsTooltipContentEmpty,
} from './ws-tooltip.types';
import { computeWsTooltipPosition } from './ws-tooltip-position.util';

@Injectable({ providedIn: 'root' })
export class WsTooltipService implements OnDestroy {
  private overlayRef: OverlayRef | null = null;
  private panelInstance: WsTooltipPanelComponent | null = null;
  private currentTrigger: HTMLElement | null = null;
  private viewContainerRef: ViewContainerRef | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollListener: (() => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private outsideTouchListener: ((event: TouchEvent) => void) | null = null;
  private overlayObserver: MutationObserver | null = null;
  private initialized = false;
  private readonly isTouchMode = classifyWineSuiteDeviceType() === 'tablet';

  constructor(private overlay: Overlay) {}

  registerViewContainerRef(viewContainerRef: ViewContainerRef): void {
    this.viewContainerRef = viewContainerRef;
  }

  init(dialog: MatDialog, overlayContainer: OverlayContainer): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    dialog.afterOpened.subscribe(() => this.closeAll());

    const container = overlayContainer.getContainerElement();
    this.overlayObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (
            node instanceof HTMLElement &&
            node.classList.contains('cdk-overlay-pane') &&
            !node.classList.contains('ws-tooltip-overlay-pane')
          ) {
            this.closeAll();
          }
        });
      }
    });
    this.overlayObserver.observe(container, { childList: true });
  }

  isOpenFor(trigger: HTMLElement): boolean {
    return this.currentTrigger === trigger && !!this.overlayRef?.hasAttached();
  }

  show(
    trigger: HTMLElement,
    content: WsTooltipContent | TemplateRef<any>,
    disabled = false,
    panelClass = '',
    context: WsTooltipTemplateContext | null = null,
    adminCopyable = false
  ): void {
    if (disabled || isWsTooltipContentEmpty(content) || !this.viewContainerRef) {
      return;
    }

    this.closeAll();
    this.currentTrigger = trigger;

    this.overlayRef = this.overlay.create({
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'ws-tooltip-overlay-pane',
      positionStrategy: this.overlay.position().global().top('0px').left('0px'),
    });

    const portal = new ComponentPortal(
      WsTooltipPanelComponent,
      this.viewContainerRef
    );
    const componentRef = this.overlayRef.attach(portal);
    this.panelInstance = componentRef.instance;
    this.panelInstance.setContent(content, context, adminCopyable);
    this.panelInstance.setPanelClass(panelClass);

    this.panelInstance.panelMouseEnter.subscribe(() => this.cancelClose());
    this.panelInstance.panelMouseLeave.subscribe(() => this.scheduleClose());

    requestAnimationFrame(() => {
      this.updatePosition();
      this.attachGlobalListeners();
    });
  }

  scheduleClose(): void {
    if (this.isTouchMode) {
      return;
    }
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.closeAll(), 100);
  }

  cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  closeAll(): void {
    this.cancelClose();
    this.detachGlobalListeners();
    this.currentTrigger = null;
    this.panelInstance = null;

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.closeAll();
    this.overlayObserver?.disconnect();
  }

  private updatePosition(): void {
    if (!this.overlayRef?.hasAttached() || !this.currentTrigger || !this.panelInstance) {
      return;
    }

    const pane = this.overlayRef.overlayElement;
    const panelEl = pane.firstElementChild as HTMLElement | null;
    if (!panelEl) {
      return;
    }

    const triggerRect = this.currentTrigger.getBoundingClientRect();
    const panelWidth = panelEl.offsetWidth;
    const panelHeight = panelEl.offsetHeight;
    const result = computeWsTooltipPosition(triggerRect, panelWidth, panelHeight);

    pane.style.position = 'fixed';
    pane.style.top = `${result.top}px`;
    pane.style.left = `${result.left}px`;

    this.panelInstance.setPlacement(result.placement, result.arrowLeft);
  }

  private attachGlobalListeners(): void {
    this.detachGlobalListeners();

    this.scrollListener = () => this.updatePosition();
    this.resizeListener = () => this.updatePosition();
    window.addEventListener('scroll', this.scrollListener, true);
    window.addEventListener('resize', this.resizeListener);

    if (this.isTouchMode) {
      this.outsideTouchListener = (event: TouchEvent) => {
        const target = event.target as Node | null;
        if (!target) {
          return;
        }
        const pane = this.overlayRef?.overlayElement;
        if (
          this.currentTrigger?.contains(target) ||
          pane?.contains(target)
        ) {
          return;
        }
        this.closeAll();
      };
      document.addEventListener('touchstart', this.outsideTouchListener, { passive: true });
    }
  }

  private detachGlobalListeners(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    if (this.outsideTouchListener) {
      document.removeEventListener('touchstart', this.outsideTouchListener);
      this.outsideTouchListener = null;
    }
  }
}
